import React, { useEffect, useMemo, useState } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { ThinkLogisticsCarrier } from './Type';
import { setCarrierSettings } from '@Reducers/User';
import {
  CSBTypeOptions,
  TermsOfInvoiceOptions,
  TaxPaymentOptions,
  IsHavingBatteryOptions,
  getWarehouses,
  batteryPackingOptions,
  batteryTypeOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Thinklogistics/constants';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import { OptionType } from '@Components/SearchDropdown/Type';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}

// ... validation schema from above ...
const iThinkValidationSchema = Yup.object().shape({
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required')
    .default([]),

  csb_type: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('CSB type value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one CSB Type is required')
    .required('CSB Type is required')
    .default([]),

  terms_of_invoice: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Terms of invoice value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Terms of invoice is required')
    .required('Terms of invoice is required')
    .default([]),

  tax_payment_option: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Tax payment option value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Tax payment option is required')
    .required('Tax payment option is required')
    .default([]),
  is_having_battery: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Is having battery value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Is having battery is required')
    .required('Is having battery  is required')
    .default([]),
  pickup_address_id: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Warehouse address value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Warehouse address is required')
    .required('Warehouse address is required')
    .default([]),
  // Conditional validation for battery fields - required only if "yes" is selected
  battery_packing: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Battery packing value is required'),
        display: Yup.string(),
      }),
    )
    .when('is_having_battery', {
      is: (is_having_battery: any[]) => is_having_battery?.some(item => item.value === 'yes'),
      then: schema => schema.min(1, 'At least one Battery packing is required').required('Battery packing is required'),
      otherwise: schema => schema.notRequired(),
    })
    .default([]),
  battery_type: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Battery type value is required'),
        display: Yup.string(),
      }),
    )
    .when('is_having_battery', {
      is: (is_having_battery: any[]) => is_having_battery?.some(item => item.value === 'yes'),
      then: schema => schema.min(1, 'At least one Battery type is required').required('Battery type is required'),
      otherwise: schema => schema.notRequired(),
    })
    .default([]),
});

const ThinkLogistics = (props: propsTypes) => {
  const { onClose, isEditable } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);
  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);
  const [pickupAddressOptions, setPickupAddressOptions] = useState<DropdownOptionType[]>([]);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  useEffect(() => {
    const accountNumber = accountOptions?.[0]?.value.split('-')[0];
    if (accountNumber) {
      getWarehouses(accountNumber).then(res => {
        setPickupAddressOptions(res);
      });
    }
  }, [accountOptions]);

  const { values, setFieldValue, errors, handleSubmit } = useFormik<ThinkLogisticsCarrier>({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: iThinkValidationSchema,
    initialValues: {
      ...carrierSetting.ithinklogistics,
    },
    onSubmit(values) {
      if (userDataById?._id) {
        if (carrierSetting.ithinklogistics._id) {
          dispatch(updateCarrierSettings({ ...carrierSetting.ithinklogistics, ...values }));
        } else {
          dispatch(addCarrierSettings({ ...carrierSetting.ithinklogistics, ...values }));
        }
      } else {
        dispatch(setCarrierSettings({ values, carrierType: 'ithinklogistics' }));
      }
      onClose();
    },
  });

  // Check if battery fields should be shown (if "yes" is selected)
  const showBatteryFields = values.is_having_battery?.some((item: OptionType) => item?.value === 'yes');

  // Handle battery selection change
  const handleBatterySelectionChange = (selectedObj: any[]) => {
    setFieldValue('is_having_battery', selectedObj);

    // If "yes" is not selected anymore, clear battery fields
    const hasYesSelected = selectedObj?.some(item => item.value === 'yes');
    if (!hasYesSelected) {
      setFieldValue('battery_packing', []);
      setFieldValue('battery_type', []);
    }
  };

  return (
    <div>
      <>
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">iThink Logistics Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label={`iThink Logistics Account`}
                    required
                    multiple={true}
                    placeholder="Select"
                    name="accountNo"
                    value={values?.accountNo?.map(item => item.value)}
                    options={accountOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={Array.isArray(errors.accountNo) ? errors.accountNo.join(', ') : errors.accountNo}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="pickup_address_id"
                    label={`Warehouse Address`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="apiServiceCode"
                    value={values?.pickup_address_id?.map(item => item.value)}
                    options={pickupAddressOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('pickup_address_id', selectedObj);
                    }}
                    error={errors.pickup_address_id as string}
                  />
                </Grid>
              </Grid>

              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="csb_type"
                    label={`Product Code`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="csb_type"
                    value={values?.csb_type?.map(item => item.value)}
                    options={CSBTypeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('csb_type', selectedObj);
                    }}
                    error={errors.csb_type as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="terms_of_invoice"
                    label={`Terms of invoice`}
                    required
                    multiple={true}
                    placeholder="Select"
                    name="terms_of_invoice"
                    value={values?.terms_of_invoice?.map(item => item.value)}
                    options={TermsOfInvoiceOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('terms_of_invoice', selectedObj);
                    }}
                    error={errors.terms_of_invoice as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="tax_payment_option"
                    label={`Tax Payment Option`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="tax_payment_option"
                    value={values?.tax_payment_option?.map(item => item.value)}
                    options={TaxPaymentOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('tax_payment_option', selectedObj);
                    }}
                    error={errors.tax_payment_option as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="is_having_battery"
                    label={`Is having battery`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="is_having_battery"
                    value={values?.is_having_battery?.map(item => item.value)}
                    options={IsHavingBatteryOptions}
                    onChange={(_, selectedObj) => {
                      handleBatterySelectionChange(selectedObj);
                    }}
                    error={errors.is_having_battery as string}
                  />
                </Grid>
              </Grid>

              {/* Conditionally render battery fields */}
              {showBatteryFields && (
                <Grid container item columnSpacing={10} rowSpacing={2}>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="battery_packing"
                      label={`Battery Packing`}
                      required
                      multiple={true}
                      placeholder="Select "
                      name="battery_packing"
                      value={values?.battery_packing?.map(item => item.value)}
                      options={batteryPackingOptions}
                      onChange={(_, selectedObj) => {
                        setFieldValue('battery_packing', selectedObj);
                      }}
                      error={errors.battery_packing as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="battery_type"
                      label={`Battery Type`}
                      required
                      multiple={true}
                      placeholder="Select "
                      name="battery_type"
                      value={values?.battery_type?.map(item => item.value)}
                      options={batteryTypeOptions}
                      onChange={(_, selectedObj) => {
                        setFieldValue('battery_type', selectedObj);
                      }}
                      error={errors.battery_type as string}
                    />
                  </Grid>
                </Grid>
              )}

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      type="submit"
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: `4px !important` }}
                      className="w-full"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Save
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        type="button"
                        sx={{ borderRadius: `4px !important` }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    </div>
  );
};

export default ThinkLogistics;
